import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { QRCodeSVG } from 'qrcode.react';

import ShareButton from '../ShareButton';
import PlayerLoader from '../PlayerLoader';
import { fetchReferralMe, fetchReferralReport } from '../../pages/player/api';
import { useLexicon } from '../../hooks/useLexicon';

// "Invite friends" block for the player profile (SMRTSTATS-351). Shows the
// user's referral link with share + QR surfaces, headline stats, and the
// per-invite list. QR is the priority surface (player-to-player, in person).
const ReferralSection = () => {
  const l = useLexicon();
  const [me, setMe] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    Promise.all([fetchReferralMe(), fetchReferralReport()])
      .then(([m, r]) => {
        if (!alive) return;
        setMe(m?.data ?? null);
        setReport(r?.data ?? null);
      })
      .catch(() => alive && setError(true))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const link = me?.link || '';
  const summary = me?.summary || report?.summary || {};
  const invites = report?.invites || [];

  return (
    <Section>
      <SectionTitle>{l(1885)}</SectionTitle>

      {loading && <PlayerLoader />}
      {error && <ErrorBox>{l(1898)}</ErrorBox>}

      {!loading && !error && (
        <>
          <Explainer>{l(1886)}</Explainer>

          <LinkCard>
            <LinkLabel>{l(1887)}</LinkLabel>
            <LinkField
              readOnly
              value={link}
              onFocus={(e) => e.target.select()}
            />
            <Actions>
              <ShareButton url={link} label={l(1861)} />
              <QrButton type="button" onClick={() => setQrOpen(true)}>
                {l(1888)}
              </QrButton>
            </Actions>
          </LinkCard>

          <Stats>
            <Stat>
              <StatNum>{summary.invited ?? 0}</StatNum>
              <StatLabel>{l(1890)}</StatLabel>
            </Stat>
            <Stat>
              <StatNum>{summary.converted ?? 0}</StatNum>
              <StatLabel>{l(1891)}</StatLabel>
            </Stat>
            <Stat>
              <StatNum>{summary.rewards_earned ?? 0}</StatNum>
              <StatLabel>{l(1892)}</StatLabel>
            </Stat>
          </Stats>

          <ListTitle>{l(1893)}</ListTitle>
          {invites.length === 0 ? (
            <Empty>{l(1894)}</Empty>
          ) : (
            invites.map((inv, i) => (
              <InviteRow key={i}>
                <InviteName>{inv.name}</InviteName>
                <Badges>
                  <Badge $on={inv.status === 'converted'}>
                    {inv.status === 'converted' ? l(1891) : l(1895)}
                  </Badge>
                  {inv.reward_status === 'granted' && <Reward>{l(1897)}</Reward>}
                  {inv.reward_status === 'pending' && <Pending>{l(1896)}</Pending>}
                </Badges>
              </InviteRow>
            ))
          )}
        </>
      )}

      {qrOpen && (
        <Overlay onClick={() => setQrOpen(false)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <Close type="button" aria-label={l(1709)} onClick={() => setQrOpen(false)}>
              ✕
            </Close>
            <ModalTitle>{l(1889)}</ModalTitle>
            <QrWrap>
              {link ? (
                <QRCodeSVG value={link} size={220} marginSize={2} level="M" />
              ) : null}
            </QrWrap>
          </Modal>
        </Overlay>
      )}
    </Section>
  );
};

export default ReferralSection;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const SectionTitle = styled.h2`
  font-family: 'SatoshiBold', sans-serif;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: ${(props) => props.theme.blueish2};
  margin: 4px 0 0;
`;

const Explainer = styled.p`
  font-family: 'SatoshiRegular', sans-serif;
  font-size: 13px;
  line-height: 1.5;
  color: ${(props) => props.theme.blueish2};
  margin: 0 0 4px;
`;

const LinkCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 16px;
  background: ${(props) => props.theme.row};
  border-radius: 8px;
`;

const LinkLabel = styled.span`
  color: ${(props) => props.theme.blueish2};
  font-size: 13px;
`;

const LinkField = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 10px 12px;
  border-radius: 6px;
  border: 1px solid ${(props) => props.theme.lines};
  background: ${(props) => props.theme.black};
  color: ${(props) => props.theme.white};
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 13px;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const QrButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid ${(props) => props.theme.borderColor};
  background: transparent;
  color: ${(props) => props.theme.white};
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 12px;
  letter-spacing: 0.3px;
  cursor: pointer;

  &:hover { border-color: ${(props) => props.theme.blueish2}; }
`;

const Stats = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 4px;
`;

const Stat = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 12px 8px;
  background: ${(props) => props.theme.row};
  border-radius: 8px;
`;

const StatNum = styled.span`
  font-family: 'SatoshiBold', sans-serif;
  font-size: 20px;
  color: ${(props) => props.theme.white};
`;

const StatLabel = styled.span`
  color: ${(props) => props.theme.blueish2};
  font-size: 11px;
  text-align: center;
`;

const ListTitle = styled.h3`
  font-family: 'SatoshiBold', sans-serif;
  font-size: 13px;
  color: ${(props) => props.theme.blueish2};
  margin: 8px 0 0;
`;

const Empty = styled.p`
  font-family: 'SatoshiRegular', sans-serif;
  font-size: 13px;
  color: ${(props) => props.theme.blueish2};
  margin: 0;
`;

const InviteRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: ${(props) => props.theme.row};
  border-radius: 8px;
`;

const InviteName = styled.span`
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 14px;
`;

const Badges = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Badge = styled.span`
  padding: 3px 10px;
  border-radius: 999px;
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 11px;
  color: ${(props) => (props.$on ? props.theme.black : props.theme.blueish2)};
  background: ${(props) => (props.$on ? props.theme.green : 'transparent')};
  border: 1px solid ${(props) => (props.$on ? props.theme.green : props.theme.lines)};
`;

const Reward = styled.span`
  padding: 3px 10px;
  border-radius: 999px;
  font-family: 'SatoshiBold', sans-serif;
  font-size: 11px;
  color: ${(props) => props.theme.black};
  background: ${(props) => props.theme.green};
`;

const Pending = styled.span`
  padding: 3px 10px;
  border-radius: 999px;
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 11px;
  color: ${(props) => props.theme.blueish2};
  border: 1px dashed ${(props) => props.theme.lines};
`;

const ErrorBox = styled.div`
  color: ${(props) => props.theme.white};
  background: #3a1414;
  padding: 12px 16px;
  border-radius: 8px;
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.7);
  padding: 24px;
`;

const Modal = styled.div`
  position: relative;
  background: ${(props) => props.theme.row};
  border: 1px solid ${(props) => props.theme.lines};
  border-radius: 12px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  max-width: 320px;
  width: 100%;
`;

const ModalTitle = styled.h3`
  font-family: 'SatoshiBold', sans-serif;
  font-size: 16px;
  margin: 0;
  color: ${(props) => props.theme.white};
`;

const QrWrap = styled.div`
  background: #ffffff;
  padding: 16px;
  border-radius: 10px;
  line-height: 0;
`;

const Close = styled.button`
  position: absolute;
  top: 10px;
  right: 12px;
  background: transparent;
  border: 0;
  color: ${(props) => props.theme.blueish2};
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
`;
